import React, { useState } from 'react';
import { Box, Card, CardContent, Typography, Button, Chip, Alert } from '@mui/material';
import { Upload as UploadIcon } from '@mui/icons-material';
import { videoAnalyzer, AI_BASE_URL } from '../../services/aiBackend';
import api from '../../services/api';

const VideoAnalyzerPage: React.FC = () => {
  const [xerFile, setXerFile] = useState<File | null>(null);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<any>(null);

  const handleXerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    setXerFile(e.target.files[0]);
  };

  const handleVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    setVideoFile(e.target.files[0]);
  };

  const runAnalysis = async () => {
    if (!xerFile || !videoFile) return;
    try {
      setLoading(true);
      setError(null);
      const res = await videoAnalyzer(xerFile, videoFile);
      setResponse(res);
      await api.post('/ai-analysis', {
        name: `Video Analysis - ${xerFile.name} & ${videoFile.name}`,
        analysisType: 'performance_analysis',
        description: 'Result from external AI backend video analyzer',
        inputParameters: { xerFileName: xerFile.name, videoFileName: videoFile.name },
        algorithms: { primary: 'neural_network' },
        dataSources: ['external_ai_backend', 'uploaded_files'],
        executionSettings: { initiatedFrom: 'frontend', externalBaseUrl: AI_BASE_URL },
        results: res.data,
      });
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to analyze video');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Video Analyzer</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      <Card>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>Upload .xer file</Typography>
          <input type="file" accept=".xer" onChange={handleXerChange} />
          {xerFile && (
            <Box sx={{ mt: 2 }}>
              <Chip label={xerFile.name} variant="outlined" />
            </Box>
          )}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>Upload video file</Typography>
            <input type="file" accept="video/*" onChange={handleVideoChange} />
            {videoFile && (
              <Box sx={{ mt: 2 }}>
                <Chip label={videoFile.name} variant="outlined" />
              </Box>
            )}
          </Box>
          {videoFile && (
            <Box sx={{ mt: 2 }}>
              <Chip label={videoFile.name} variant="outlined" />
            </Box>
          )}
          <Box sx={{ mt: 2 }}>
            <Button variant="contained" startIcon={<UploadIcon />} disabled={!xerFile || !videoFile || loading} onClick={runAnalysis}>
              Run Analysis
            </Button>
          </Box>
          {response && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2">Response Status: {response.status}</Typography>
              <pre style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(response.data, null, 2)}</pre>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default VideoAnalyzerPage;